import { code128BarcodeSvg } from "./code128";
import {
  formatTransferDate,
  type InventoryReplenishmentRecord,
} from "./inventory-transfer-view";

const LABELS_PER_PAGE = 18;

export type InventoryReplenishmentPrintRecord = Pick<InventoryReplenishmentRecord,
  "productTitle" | "variantTitle" | "quantity" | "barcodeValue" | "requestType" | "customerName" | "visitAt"> & { groupName?: string };

function escapeHtml(value: unknown): string {
  return String(value)
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}

function printProductTitle(value: string): string {
  const normalized = value.trim();
  const separatorIndex = normalized.indexOf("・");
  if (separatorIndex < 0) return normalized;
  return normalized.slice(separatorIndex + 1).trim() || normalized;
}

export function inventoryReplenishmentPrintPages(
  records: readonly InventoryReplenishmentPrintRecord[],
  usedFirstPageSlots: readonly number[] = [],
): string[] {
  if (!records.length) return [];
  const usedSlots = new Set(usedFirstPageSlots.filter((slot) => Number.isInteger(slot) && slot >= 1 && slot <= LABELS_PER_PAGE));
  const pages: Array<Array<InventoryReplenishmentPrintRecord | undefined>> = [];
  let recordIndex = 0;
  while (recordIndex < records.length) {
    const firstPage = pages.length === 0;
    pages.push(Array.from({ length: LABELS_PER_PAGE }, (_, index) => {
      if (firstPage && usedSlots.has(index + 1)) return undefined;
      return records[recordIndex++];
    }));
  }
  return pages.map((pageRecords, pageIndex) => {
    const labels = Array.from({ length: LABELS_PER_PAGE }, (_, slotIndex) => {
      const slot = slotIndex + 1;
      const used = pageIndex === 0 && usedSlots.has(slot);
      const control = pageIndex === 0 ? `<label class="inventory-label-slot-control" title="${slot}番の枠を使用済みにする"><input type="checkbox" data-label-used-slot="${slot}" aria-label="${slot}番の枠を使用済みにする" ${used ? 'checked' : ''}><span>${slot}</span></label>` : '';
      const record = pageRecords[slotIndex];
      if (!record) {
        return `<section class="inventory-replenishment-label is-empty${used ? ' is-used' : ''}" data-replenishment-label-slot="${slot}"${pageIndex ? ' aria-hidden="true"' : ''}>${control}${used ? '<span class="inventory-label-slot-note">使用済み・印刷しません</span>' : ''}</section>`;
      }
      const requestType = record.requestType?.trim() || "—";
      const isStockReplenishment = requestType === "在庫補充";
      const groupName = record.groupName?.trim();
      const requestHeading = groupName ? `(${groupName}) ${requestType}` : requestType;
      const productName = [printProductTitle(record.productTitle), record.variantTitle.trim()]
        .filter(Boolean)
        .join(" ／ ") || "—";
      const customerName = record.customerName?.trim() || "";
      const visitAt = record.visitAt?.trim() ? formatTransferDate(record.visitAt) : "";
      const customerAndVisit = [
        customerName ? `<span>${escapeHtml(customerName.endsWith("様") ? customerName : `${customerName} 様`)}</span>` : "",
        visitAt ? `<span><b>来店日時</b> ${escapeHtml(visitAt)}</span>` : "",
      ].filter(Boolean).join("");

      return `<section class="inventory-replenishment-label${isStockReplenishment ? ` is-stock-replenishment${customerAndVisit ? " has-customer-visit" : ""}` : ""}" data-replenishment-label-slot="${slotIndex + 1}">
        ${control}
        <p class="inventory-replenishment-label-request-type"><strong>${escapeHtml(requestHeading)}</strong></p>
        <p class="inventory-replenishment-label-product" title="${escapeHtml(productName)} ×${record.quantity}"><span>${escapeHtml(productName)}</span><strong>×${record.quantity}</strong></p>
        ${isStockReplenishment ? '<p class="inventory-replenishment-label-stock-notice">【補充分】</p>' : ''}
        ${customerAndVisit || !isStockReplenishment ? `<p class="inventory-replenishment-label-customer-visit">${customerAndVisit || "&nbsp;"}</p>` : ''}
        <div class="inventory-replenishment-label-barcode">${code128BarcodeSvg(record.barcodeValue)}</div>
      </section>`;
    }).join("");

    return `<article class="inventory-replenishment-print-page">
      <div class="inventory-replenishment-label-grid">${labels}</div>
    </article>`;
  });
}
